/**
 * Time Machine #114 — pure replay function shared between frontend (Phase 4.5
 * in-memory slider scrub) and dbtools (Phase 3 forensic CLI).
 *
 * Plain English:
 *   - Snapshot = a photograph of the whole collection at one moment.
 *   - EventLog event = a diary entry for one change (create/update/delete).
 *   - Replay = start with the photo, walk the diary forward in time, apply
 *     every change up to the target moment. What's left is the answer.
 *
 * Pure / side-effect-free / deterministic — no DB calls, no I/O. The dbtools
 * mirror at `dbtools/src/utility/replayState.mjs` is structurally equivalent
 * (function body and counter shape match exactly; comments and module syntax
 * differ since Jest uses ESM-via-Babel and dbtools uses Node ESM with
 * node:test). Kept in sync manually — any change to the function body here
 * must mirror there and vice-versa.
 *
 * Why we use `ev.after` directly on updates (instead of merging `updateFields`
 * into the seed doc): the Atlas trigger captures the full post-image on every
 * mutation, so `after` is already "the doc as it is after this change." Using
 * it skips an entire class of bugs around `removedFields`, nested-merge
 * semantics, and create-then-delete-then-recreate sequences within the window.
 */

/**
 * Apply a stream of EventLog events to a seed state, in chronological order.
 *
 * @param {Map<string, object>} seedState  Initial state, keyed by documentId.toString().
 *                                          Caller owns this; we copy it so we don't mutate.
 * @param {Array<object>} events            EventLog records, sorted ascending by timestamp.
 *                                          Each must have { documentId, operation, after? }.
 * @returns {{
 *   state: Map<string, object>,
 *   applied: {
 *     create: number,
 *     update: number,
 *     delete: number,
 *     createOverwrote: number,    // create event for an id already in state — shouldn't normally happen
 *     deleteMissing: number,      // delete event for an id not in state — possible if create+delete in window
 *     skippedNoId: number,        // event with no documentId — defensive, should be 0
 *     skippedNoAfter: number,     // create/update event with null after — defensive, should be 0
 *   }
 * }}
 */
export function replayState(seedState, events) {
  const state = new Map(seedState);
  const applied = {
    create: 0,
    update: 0,
    delete: 0,
    createOverwrote: 0,
    deleteMissing: 0,
    skippedNoId: 0,
    skippedNoAfter: 0,
  };

  for (const ev of events) {
    const id = ev.documentId?.toString();
    if (!id) {
      applied.skippedNoId++;
      continue;
    }

    switch (ev.operation) {
      case "create": {
        if (!ev.after) { applied.skippedNoAfter++; break; }
        if (state.has(id)) applied.createOverwrote++;
        state.set(id, ev.after);
        applied.create++;
        break;
      }
      case "update": {
        if (!ev.after) { applied.skippedNoAfter++; break; }
        state.set(id, ev.after);
        applied.update++;
        break;
      }
      case "delete": {
        if (!state.has(id)) applied.deleteMissing++;
        state.delete(id);
        applied.delete++;
        break;
      }
      default:
        // Unknown operation — ignore. Future-compat: if Atlas trigger ever emits
        // a "replace" op or similar, we'd add it here.
        break;
    }
  }

  return { state, applied };
}
